import { jwtDecode } from 'jwt-decode';
import { apiFetch } from '../providers/api-rest-provider';
import { getIdToken, getSessionRevision } from '../providers/cognito';
import type { QueueAccess } from '../utils/offline-queue';

interface CaptureClaims {
  sub?: unknown;
  tenantId?: unknown;
}

interface CaptureContextResponse {
  tenantId?: unknown;
  actorId?: unknown;
}

interface MeResponse {
  id?: unknown;
}

export interface CaptureSession {
  tenantId: string;
  actorId: string;
  instanceId: string;
  eventId: string;
  scope: string;
  sessionToken: string;
  sessionRevision: number;
}

const requiredString = (value: unknown, label: string): string => {
  if (typeof value !== 'string' || !value.trim()) {
    throw new Error(`Capture session is missing ${label}`);
  }
  return value;
};

const claimsFor = (token: string): { actorId: string; tenantId: string } => {
  let claims: CaptureClaims;
  try {
    claims = jwtDecode<CaptureClaims>(token);
  } catch {
    throw new Error('Capture session token is invalid');
  }
  return {
    actorId: requiredString(claims.sub, 'the signed JWT subject'),
    tenantId: requiredString(claims.tenantId, 'the signed JWT tenant claim'),
  };
};

export const captureQueueScope = (
  tenantId: string,
  actorId: string,
  instanceId: string,
  eventId: string,
): string => `batch-grade:${[
  tenantId,
  actorId,
  instanceId,
  eventId,
].map(encodeURIComponent).join(':')}`;

export function isCaptureSessionCurrent(session: CaptureSession): boolean {
  const token = getIdToken();
  if (
    token !== session.sessionToken ||
    getSessionRevision() !== session.sessionRevision
  ) return false;
  try {
    const claims = claimsFor(token);
    return claims.actorId === session.actorId && claims.tenantId === session.tenantId;
  } catch {
    return false;
  }
}

export function isCaptureSessionFor(
  session: CaptureSession | null,
  instanceId: string,
  eventId: string,
): session is CaptureSession {
  return Boolean(
    session &&
    session.instanceId === instanceId &&
    session.eventId === eventId &&
    isCaptureSessionCurrent(session),
  );
}

export function requireCurrentCaptureSession(session: CaptureSession): void {
  if (!isCaptureSessionCurrent(session)) {
    throw Object.assign(
      new Error('Capture session changed; queued work remains with its original owner'),
      { status: 401 },
    );
  }
}

export const captureQueueAccess = (session: CaptureSession): QueueAccess => ({
  scope: session.scope,
  sessionToken: session.sessionToken,
  isCurrent: () => isCaptureSessionCurrent(session),
});

export async function submitCaptureOperation<
  T extends { client_op_id: string },
>(
  session: CaptureSession,
  payload: T,
  operationId: string,
): Promise<unknown> {
  if (payload.client_op_id !== operationId) {
    throw new Error('Queued operation identity does not match its immutable payload');
  }
  requireCurrentCaptureSession(session);
  const receipt = await apiFetch('attempts', {
    method: 'POST',
    body: JSON.stringify(payload),
  }, session.sessionToken);
  requireCurrentCaptureSession(session);
  return receipt;
}

export async function loadCaptureSession(
  instanceIdValue: string,
  eventIdValue: string,
): Promise<CaptureSession> {
  const instanceId = requiredString(instanceIdValue, 'an assessment instance');
  const eventId = requiredString(eventIdValue, 'an event');
  const sessionToken = requiredString(getIdToken(), 'an authenticated token');
  const sessionRevision = getSessionRevision();
  const claims = claimsFor(sessionToken);

  const assertPinned = () => {
    if (
      getIdToken() !== sessionToken ||
      getSessionRevision() !== sessionRevision
    ) throw new Error('Capture session changed while verifying its owner');
  };

  assertPinned();
  const me = await apiFetch('me', {}, sessionToken) as MeResponse;
  assertPinned();
  if (requiredString(me?.id, '/me identity') !== claims.actorId) {
    throw new Error('/me identity conflicts with the signed JWT subject');
  }

  assertPinned();
  const context = await apiFetch(
    'attempts/capture-context',
    {},
    sessionToken,
  ) as CaptureContextResponse;
  assertPinned();
  if (requiredString(context?.actorId, 'the server capture actor') !== claims.actorId) {
    throw new Error('Server capture actor conflicts with the signed JWT subject');
  }
  if (requiredString(context?.tenantId, 'the server capture tenant') !== claims.tenantId) {
    throw new Error('Server capture tenant conflicts with the signed JWT tenant claim');
  }

  return {
    tenantId: claims.tenantId,
    actorId: claims.actorId,
    instanceId,
    eventId,
    scope: captureQueueScope(
      claims.tenantId,
      claims.actorId,
      instanceId,
      eventId,
    ),
    sessionToken,
    sessionRevision,
  };
}
